import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import { Activity, useMemo, useState } from 'react';
import SSOAttributeRuleModal from './sso-attribute-rule-modal';
import DeleteRuleModal from './delete-rule-modal';
import useComputedRegistrationSettings from '../../../hooks/use-computed-registration-settings';
import useLoadSSOs from '../../../hooks/use-load-ssos';
import deriveActiveGateTypes from '../../gating/helpers/derive-active-gate-types';
import { GateTypes, RegistrationTypes, SSOAttributeRule, SSOOptionsType } from '@/types/registration.types';
import OptionalComponent from '@ui/optional-component/optional-component';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { getUser } from '@/store/slices/user/selectors';
import TextAlert from '@ui/text-alert/text-alert';
import TextAlertTitle from '@ui/text-alert/text-alert-title';
import { getDefaultSSOAttributeRule, isValidAttributeRule } from '@/utils/authentication/sso-utils';
import RenderRule from './render-rule';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import Text from '@ui/text/text/text';
import { TooltipPortalContentDirection } from '@ui/tooltip-portal/tooltip-portal-types';

export default function AccessControlSettings() {
  const dispatch = useAppDispatch();

  const user = useAppSelector(getUser);
  const [selectedRuleToEdit, setSelectedRuleToEdit] = useState<SSOAttributeRule>(getDefaultSSOAttributeRule(user?.id));
  const [selectedRuleToDelete, setSelectedRuleToDelete] = useState<SSOAttributeRule | null>(null);
  const [showAddRuleModal, setShowAddRuleModal] = useState(false);
  const { registrationSettings } = useComputedRegistrationSettings();

  const { ssos } = useLoadSSOs();

  const selectedCustomSso = useMemo(() => {
    return ssos.find((sso: SSOOptionsType) => registrationSettings?.singleSignOn?.singleSignOns?.includes(sso.uuid));
  }, [registrationSettings?.singleSignOn?.singleSignOns, ssos]);

  const rules = useMemo(() => {
    return registrationSettings?.singleSignOn?.rules?.toSorted((a, b) => b.createdAt.localeCompare(a.createdAt)) ?? [];
  }, [registrationSettings?.singleSignOn?.rules]);

  // Rules are mutually exclusive with the audience list gate (BDEV-5154). While the gate is
  // active, adding rules and toggling existing ones is blocked (Edit/Delete stay available so
  // legacy data with both can be untangled). SsoAudienceListGate blocks the reverse direction.
  const audienceGateActive = useMemo(
    () =>
      registrationSettings?.type === RegistrationTypes.gated &&
      deriveActiveGateTypes(registrationSettings).includes(GateTypes.passcode_list),
    [registrationSettings]
  );

  const handleAddRule = () => {
    setSelectedRuleToEdit(getDefaultSSOAttributeRule(user?.id));
    setShowAddRuleModal(true);
  };

  const handleToggleRule = (uuid: string, on: boolean) => {
    if (!registrationSettings?.singleSignOn) return;
    const updatedRules = rules.map((rule) =>
      rule.uuid === uuid ? { ...rule, enabled: on, updated_at: new Date().toISOString(), updated_by: user?.id } : rule
    );
    dispatch(
      saveWorkingProjectByFields({
        registration_settings: {
          ...registrationSettings,
          singleSignOn: { ...registrationSettings?.singleSignOn, rules: updatedRules },
        },
      })
    );
  };

  const handleConfirmDeleteRule = () => {
    if (!registrationSettings?.singleSignOn || !selectedRuleToDelete) return;
    const updatedRules = rules.filter((rule) => rule.uuid !== selectedRuleToDelete.uuid);
    dispatch(
      saveWorkingProjectByFields({
        registration_settings: {
          ...registrationSettings,
          singleSignOn: { ...registrationSettings?.singleSignOn, rules: updatedRules },
        },
      })
    );
    setSelectedRuleToDelete(null);
  };

  const handleEditRule = (uuid: string) => {
    const rule = rules.find((rule) => rule.uuid === uuid);
    if (!rule) return;
    setSelectedRuleToEdit(rule);
    setShowAddRuleModal(true);
  };

  const handleEditRuleClose = () => {
    setSelectedRuleToEdit(getDefaultSSOAttributeRule(user?.id));
    setShowAddRuleModal(false);
  };

  const allRulesValid = useMemo(() => {
    return rules.every((rule) => selectedCustomSso && isValidAttributeRule(rule, selectedCustomSso));
  }, [rules, selectedCustomSso]);

  if (!selectedCustomSso) return null;
  return (
    <div className="flex-column gap-8">
      <Container
        design="GlobalCont1"
        borderRadius="12px"
        padding="16px"
        className="flex fd-column gap-8"
        border={allRulesValid ? 'none' : '1px solid var(--global-error-500)'}
      >
        <div className="flex ai-center jc-space-between">
          <Text tag="h6" className="H6">
            Rules & Conditions
          </Text>
          <div>
            <TooltipPortal
              disabled={!audienceGateActive}
              contentDirection={TooltipPortalContentDirection.RTL}
              verticalOffset={-4}
              keepOpenOnContentHover={false}
              tooltipContent={
                <Text design="GlobalIcoText1" className="P2" maxWidth="250px">
                  Rules & Conditions can't be combined with an Audience List gate. Turn off the Audience List gate to
                  add rules.
                </Text>
              }
            >
              <Button
                design="LocalBttnSec"
                designSize="small"
                className="flex ai-center jc-center gap-6"
                onClick={handleAddRule}
                disabled={audienceGateActive}
              >
                <Icon name={ICONS.ADD} size={12} />
                <Text>Add rule</Text>
              </Button>
            </TooltipPortal>
          </div>
        </div>
        <Activity mode={allRulesValid ? 'hidden' : 'visible'}>
          <TextAlert severity="error">
            <TextAlertTitle>Invalid rules</TextAlertTitle>
            <Text className="C2">Your SSO Configuration has changed, invalidating your existing ruleset</Text>
          </TextAlert>
        </Activity>
        <Activity mode={audienceGateActive && rules.length > 0 ? 'visible' : 'hidden'}>
          <TextAlert severity="warning">
            <TextAlertTitle>Audience List gate is on</TextAlertTitle>
            <Text className="C2">
              This project has both an Audience List gate and SSO rules. Registrants must pass any enabled rules AND be
              on an audience list. Delete the rules or turn off the gate to use only one.
            </Text>
          </TextAlert>
        </Activity>
        <OptionalComponent display={rules.length > 0}>
          <Text className="S1" design="GlobalIcoText3">
            Attribute Rules
          </Text>
          <>
            {rules.map((rule) => (
              <RenderRule
                key={rule.uuid}
                rule={rule}
                selectedCustomSso={selectedCustomSso}
                handleToggleRule={handleToggleRule}
                handleEditRule={handleEditRule}
                setSelectedRuleToDelete={setSelectedRuleToDelete}
                toggleDisabled={audienceGateActive}
              />
            ))}
          </>
        </OptionalComponent>
      </Container>
      <SSOAttributeRuleModal
        open={showAddRuleModal}
        onClose={handleEditRuleClose}
        existingRule={selectedRuleToEdit}
        selectedCustomSSO={selectedCustomSso as SSOOptionsType}
      />
      <DeleteRuleModal
        open={!!selectedRuleToDelete}
        onClose={() => setSelectedRuleToDelete(null)}
        onDelete={handleConfirmDeleteRule}
      />
    </div>
  );
}
